import React from 'react';
import { useTranslation } from 'react-i18next';

import { PERMISSIONS } from 'api/auth/constants';
import { INITIAL_OFF_COLUMNS_CLIENT_INSTRUMENTS } from 'api/clients/constants';
import DataGrid from 'components/Grids/DataGrid';
import Loader from 'components/Loader';
import PageContainer from 'components/PageContainer';
import { usePageNavigation } from 'hooks/usePageNavigation';
import { IUsePageNavigation } from 'hooks/usePageNavigation/types';
import { useRBAC } from 'hooks/useRBAC';
import { useGetClientsInstruments } from 'queries/clients';
import { ROUTES } from 'routes/config/constants';
import { LoaderContainer } from 'routes/styled';

import { useClientInstrumentsGridData } from './useClientInstrumentsGridData';

const ClientsInstrumentsList = () => {
  const { t } = useTranslation();

  const { checkPermission } = useRBAC();

  const pageNavigationConfig = React.useMemo(() => {
    const config: IUsePageNavigation = {
      hiddenColumns: {
        cardFullBin: !checkPermission({
          list: [PERMISSIONS.VIEW_FULL_BIN],
        }),
        'customer.email': !checkPermission({
          list: [PERMISSIONS.VIEW_EMAILS],
        }),
        'merchantTerminal.merchant.name': !checkPermission({
          list: [PERMISSIONS.VIEW_ORGANIZATIONS],
        }),
      },
      pageKey: ROUTES.CLIENTS_MANAGEMENT.SUB_PATH.CLIENTS_INSTRUMENTS.PATH,
    };

    return config;
  }, []);

  const pageFiltersConfig = usePageNavigation(pageNavigationConfig);

  const { data, failureCount, isFetching, isLoading, refetch } = useGetClientsInstruments(
    pageFiltersConfig.pageFilters,
  );

  const { columns, gridFilters, sectionsFiltersMap } = useClientInstrumentsGridData({
    data: data?.data,
    filters: data?.filters,
    hiddenColumns: pageNavigationConfig.hiddenColumns,
  });

  const filtersConfig = React.useMemo(
    () => ({
      failureCount,
      filtersSections: gridFilters,
      filtersSectionsMap: sectionsFiltersMap,
      intlPrefix: 'clientsInstruments.columns',
      requestFiltersPrefix: 'clients/payment-instruments',
    }),
    [gridFilters, sectionsFiltersMap, failureCount],
  );

  if (isLoading) {
    return (
      <LoaderContainer>
        <Loader size={100} />
      </LoaderContainer>
    );
  }

  return (
    <PageContainer title={t('clientsInstruments.title')}>
      <DataGrid
        baseOffColumns={INITIAL_OFF_COLUMNS_CLIENT_INSTRUMENTS}
        columns={columns}
        data={data?.data || []}
        filters={data?.filters}
        filtersConfig={filtersConfig}
        isBottomToolbar={data?.hasResults}
        isFetching={isFetching}
        isLoading={isLoading}
        isTopToolbar={data?.hasResults}
        pageFiltersConfig={pageFiltersConfig}
        refetch={refetch}
        searchString={data?.searchString}
        sort={data?.sort}
        total={data?.totalElements}
      />
    </PageContainer>
  );
};

export default ClientsInstrumentsList;
